import { useCallback, useEffect, useRef, useState } from 'react'

import { useSearchParams } from 'hooks'
import { useCurrentLanguage } from 'lib/tolgee'
import { FeatureFlagsStore, PanelStore } from 'stores'

import { IFRAME_TIMEOUT } from './zai-iframe-provider.consts'
import type { IZaiIframeProviderModel, IZaiIframeProviderProps } from './zai-iframe-provider.types'
import { ZAI_CHAT_PARAMS_KEY } from '../common/zai.consts'
import { zaiUtils } from '../common/zai.utils'

export const useZaiIframeProviderModel = ({ iframeRef }: IZaiIframeProviderProps): IZaiIframeProviderModel => {
  const workspaceId = Object(PanelStore.selectedPanel)?.id
  const language = useCurrentLanguage()
  const searchParams = useSearchParams()

  const [hasError, setHasError] = useState(false)
  const [isLoading, setIsLoading] = useState(true)

  const timeoutRef = useRef<NodeJS.Timeout>()

  const threadId = searchParams.get(ZAI_CHAT_PARAMS_KEY.threadId)
  const threadPrompt = searchParams.get(ZAI_CHAT_PARAMS_KEY.threadPrompt)
  const threadPromptPage = searchParams.get(ZAI_CHAT_PARAMS_KEY.threadPromptPage)
  const quickPromptId = searchParams.get(ZAI_CHAT_PARAMS_KEY.quickPromptId)

  const isZaiRagEnabled = FeatureFlagsStore.getFeatureFlag('isZaiRagEnabled')

  const handleIframeLoad = useCallback(() => {
    setIsLoading(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleIframeError = useCallback(() => {
    setHasError(true)
    setIsLoading(false)
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
  }, [])

  const handleRetry = useCallback(() => {
    setHasError(false)
    setIsLoading(true)

    if (iframeRef.current) {
      const src = iframeRef.current.src
      iframeRef.current.src = ''
      setTimeout(() => {
        if (iframeRef.current) {
          iframeRef.current.src = src
        }
      }, 100)
    }

    timeoutRef.current = setTimeout(() => {
      setHasError(true)
      setIsLoading(false)
    }, IFRAME_TIMEOUT)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onThreadIdChanges = useCallback(
    (event: MessageEvent) => {
      const currentUrl = new URL(window.location.href)

      if (
        !zaiUtils.isIframeOriginValid(event, iframeRef.current?.src) ||
        event.data?.action !== 'on-thread-changes' ||
        event.data?.origin !== 'zai-chat'
      )
        return

      currentUrl.searchParams.set(ZAI_CHAT_PARAMS_KEY.threadId, event.data?.chat?.threadId)

      window.history.replaceState(null, '', currentUrl.toString())
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  const onThreadPromptChanges = useCallback((event: MessageEvent) => {
    const currentUrl = new URL(window.location.href)

    if (
      !zaiUtils.isIframeOriginValid(event, iframeRef.current?.src) ||
      event.data?.action !== 'on-thread-changes' ||
      event.data?.origin !== 'zai-chat' ||
      !event.data?.chat?.isPromptInitialized
    )
      return

    currentUrl.searchParams.delete(ZAI_CHAT_PARAMS_KEY.threadPrompt)
    currentUrl.searchParams.delete(ZAI_CHAT_PARAMS_KEY.threadPromptPage)
    currentUrl.searchParams.delete(ZAI_CHAT_PARAMS_KEY.quickPromptId)

    window.history.replaceState(null, '', currentUrl.toString())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    window.removeEventListener('message', onThreadIdChanges)

    window.addEventListener('message', onThreadIdChanges)

    return () => window.removeEventListener('message', onThreadIdChanges)
  }, [onThreadIdChanges])

  useEffect(() => {
    window.removeEventListener('message', onThreadPromptChanges)

    window.addEventListener('message', onThreadPromptChanges)

    return () => window.removeEventListener('message', onThreadPromptChanges)
  }, [onThreadPromptChanges])

  useEffect(() => {
    timeoutRef.current = setTimeout(() => {
      setHasError(true)
      setIsLoading(false)
    }, IFRAME_TIMEOUT)

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  return {
    workspaceId,
    language,
    handleRetry,
    handleIframeLoad,
    handleIframeError,
    hasError,
    isLoading,
    threadId,
    threadPrompt,
    threadPromptPage,
    quickPromptId,
    isZaiRagEnabled,
  }
}
